import React from "react";
import { useQuery } from "urql";

import FullscreenLoader from "@/components/fullscreen-loader";
import { CURRENT_USER_QUERY } from "@/features/account/graphql/current-user-query";
import { MemberRole, type ApplicationRole } from "@/gql/graphql";

type CurrentUser = {
  avatarUrl: string;
  emailAddress: string;
  fullName: string;
  initials: string;
  id: string;
  phoneNumber: string;
  role: ApplicationRole;
  timezone: string;
  memberships: Membership[];
};

export type Membership = {
  defaultMapCenter: {
    latitude: number;
    longitude: number;
  };
  defaultMapZoom: number;
  groups: string[];
  id: string;
  isAdmin: boolean;
  isAuditor: boolean;
  isCheckInSupervisor: boolean;
  isGuardMeSupervisor: boolean;
  isIncidentSupervisor: boolean;
  isOwner: boolean;
  logoUrl: string;
  memberId: string;
  name: string;
  plan: string;
  role: MemberRole;
  timezone: string;
};

export type CurrentUserContextType = {
  currentUser: CurrentUser;
};

const CurrentUserContext = React.createContext<CurrentUserContextType | null>(null);

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = React.useState<CurrentUser | null>(null);

  const [{ data, error, fetching }] = useQuery({ query: CURRENT_USER_QUERY });

  React.useEffect(() => {
    if (data?.currentUser) {
      let memberships = data
        .currentUser!.memberships.nodes.map((x) => ({
          defaultMapCenter: x.organization!.defaultMapCenter,
          defaultMapZoom: x.organization!.defaultMapZoom,
          groups: x.groups.nodes.map((x) => x.id),
          id: x.organizationId,
          isAdmin: [MemberRole.Owner, MemberRole.Administrator].includes(x.organizationRole),
          isAuditor: false,
          isCheckInSupervisor: x.isCheckInSupervisor ?? false,
          isGuardMeSupervisor: x.isGuardmeSupervisor ?? false,
          isIncidentSupervisor: x.isIncidentSupervisor ?? false,
          isOwner: x.organizationRole === MemberRole.Owner,
          logoUrl: x.organization!.logoUrl,
          memberId: x.id,
          name: x.organization!.name,
          plan: x.organization!.plan!.name,
          role: x.organizationRole,
          timezone: x.organization!.timezone,
        }))
        .sort((a, b) => (a.name < b.name ? -1 : 1));

      setCurrentUser({
        avatarUrl: data.currentUser.avatarUrl,
        emailAddress: data.currentUser.emailAddress ?? "",
        fullName: data.currentUser.fullName,
        id: data.currentUser.id,
        phoneNumber: data.currentUser.phoneNumber ?? "",
        role: data.currentUser.applicationRole,
        timezone: data.currentUser.timezone,
        memberships: memberships,
        initials: data.currentUser.fullName.slice(0, 2).toUpperCase(),
      });
    }
  }, [data?.currentUser]);

  if (fetching && currentUser === null) {
    return <FullscreenLoader message="Loading User..." />;
  }

  if (error) {
    return <div>User Error!</div>;
  }

  if (!currentUser) {
    return <div>Current User not loaded!</div>;
  }

  return (
    <CurrentUserContext.Provider value={{ currentUser }}>{children}</CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = React.useContext(CurrentUserContext);
  if (!context) throw new Error("useCurrentUser must be used within an CurrentUserProvider");
  return context;
}
